// frontend/src/pages/Dashboard.tsx
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import EmailVerificationNotification from '@/components/common/EmailVerificationNotification';

const Dashboard: React.FC = () => {
  const { user, emailVerificationStatus } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-first container with better spacing */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
        {/* Email Verification Notification */}
        <EmailVerificationNotification className="mb-4" />

        {/* Page header - Mobile optimized */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
            Welcome back, {user?.first_name}!
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            <span className="capitalize">{user?.role}</span> Dashboard
          </p>
        </div>

        {/* Main content - Lighter shadow, better mobile padding */}
        <div className="bg-white border border-gray-200 rounded-lg sm:shadow-sm">
          <div className="p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900">
              Getting Started
            </h3>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              {user?.role === 'provider' 
                ? 'Complete your profile and set up your services to start receiving bookings.'
                : 'Explore our services and book your first appointment.'
              }
            </p>
            <div className="mt-4">
              <button
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {user?.role === 'provider' ? 'Complete Profile' : 'Browse Services'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid - Mobile-first with better spacing */}
        <div className="mt-4 sm:mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Quick stats or action cards */}
          <div className="bg-white border border-gray-200 rounded-lg sm:shadow-sm">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">1</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {user?.role === 'provider' ? 'Services Listed' : 'Bookings Made'}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">0</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg sm:shadow-sm">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">2</span>
                  </div>
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500 truncate">
                    Profile Completion
                  </p>
                  <p className="text-lg font-medium text-gray-900">25%</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg sm:shadow-sm">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">3</span>
                  </div>
                </div>
                <div className="ml-4 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500 truncate">
                    {user?.role === 'provider' ? 'Pending Reviews' : 'Upcoming Bookings'}
                  </p>
                  <p className="text-lg font-medium text-gray-900">0</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;